import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as RDPD from "reports/ReportDataProcessorDef";
import * as SR from "reports/measures/SeverityResolver";

/**
 * The Trajectory type acts as a container for the full history of measurements for a single Outcome Measure.
 *
 * Its member properties describe, in various formats, the progress made by the patient in that measure,
 * for use when creating a patient Outcome Report.
 */
export type Trajectory = {
    /**
     * The measure type (e.g. LeftShoulderScaption)
     */
    measureType: MD.MeasureType;

    /**
     * Friendly measure display name (e.g. "Left Shoulder Scaption")
     */
    measureName: string;

    // ---=== Inputs ===--- //

    /**
     * The set of weekly-best measurements for the measure, from the first week through the Report Week.
     *
     * These values are used to calculate the trajectory results.
     */
    measures: MD.Measures;

    /**
     * The “up to date” measurement for the selected Report Week.
     *
     * For non-ROM measures, this is the last value in measures.
     * For ROM measures, this is the recent best going back up to 3 weeks from the most recent measurement.
     * This value is compared against the first measurement in measures when calculating the trajectory.
     */
    comparedMeasure: MD.Measure;

    /**
     * The earliest all-time measurement which was not recorded in Week Zero (only used for ROM).
     *
     * This may or may not be the first value in measures–this is the first all-time measurement,
     * while measures contains weekly bests.
     */
    firstNonWeekZeroMeasure?: MD.Measure;

    // ---=== Results ===--- //

    /**
     * The raw value change between the first and last measurements in measures (may be +/=).
     * */
    rawDelta: number;

    /**
     * Describes whether the last measurement in measures has improved, worsened, or is unchanged in
     * its raw value from the first measurement.
     */
    rawTrajectory: TrajectoryType;

    /**
     * The delta between the first measurement in measures and comparedMeasure (may be +/=).
     *
     * For non-ROM measures, this value is not scaled.
     * For ROM measures, this value is normalized as follows:
     *   Upper Extremity: 0-180
     *   Lower Back: 0-90
     *   Neck:
     *     Extension: 0-60
     *     Flexion: 0-50
     *     Rotation (Left & Right): 0-80
     */
    comparedDelta: number;

    /**
     * Describes whether the compared measurement has improved, worsened, or is unchanged in
     * its raw value from the first measurement.
     */
    bandTrajectory: TrajectoryType;

    /**
     * Describes whether the compared measurement is in a different score band to the first
     * measurement (improved, worsened, unchanged).
     *
     * See SeverityResolver for the score bands used for each measure.
     */
    bandSeverityTrajectory: TrajectoryType;

    /**
     * The delta between the raw values of the first and last measurements (may be +/=).
     */
    normalizedDelta: number;

    /**
     * The values of each measurement in measures where the measure’s min/max range has been
     * normalized into a standard 0-100 scale.
     *
     * This normalization process allows measures with different min/max ranges to be plotted
     * on the same graph.
     */
    normalizedMeasures: number[];
};

export type Trajectories = Trajectory[];

export enum TrajectoryType {
    WORSENED = -1,
    UNCHANGED = 0,
    IMPROVED = 1
}

export interface TrajectoryProcessor {
    measureType: MD.MeasureType;
    measures: MD.Measures;
    firstMeasurement?: RDPD.FirstWeeklyMeasurement;
    comparedMeasureRange?: number;
}

export const ComparedRomMeasureRangeDefault = 3;

// ---=== Utilities ===--- //

export const hasTrajectories = (trajectories: Trajectories): boolean => {
    return trajectories?.length > 0 && trajectories.filter(mt => mt.bandSeverityTrajectory === TrajectoryType.IMPROVED).length > 0;
};

export const hasTrajectoryOfType = (measureType: MD.MeasureType, TrajectoryTypes: MD.MeasureTypes): boolean => {
    return TrajectoryTypes?.includes(measureType);
};

export const getTrajectoryOfType = (measureType: MD.MeasureType, trajectories: Trajectories): Trajectory => {
    return trajectories.find(mt => mt.measureType === measureType) ?? null;
};

export const firstMeasure = (t: Trajectory) => {
    return M.isRomType(t.measureType) ? t.firstNonWeekZeroMeasure : t.measures[0]
}

export const isFunctional = (t: Trajectory) => {
    let resolver: SR.SeverityResolver = MP.MeasurePropertiesMap[t.measureType].severityResolver;
    return resolver(t.comparedMeasure.value).functional;
}

export const clipMeasureValues = (measureType: MD.MeasureType, measures: MD.Measures) => {
    let measureProperties: MP.MeasureProperties = MP.MeasurePropertiesMap[measureType];
    return measures.map(measure => {
        return {
            ...measure,
            value: Math.min(Math.max(measure.value, 0), measureProperties.maxScore)
        }
    })
}
// ---=== Calculation Helpers ===--- //

export const calculateRawTrajectory = (measures: MD.Measures, comparedMeasure: MD.Measure = null): TrajectoryType => {
    const rawDelta = M.rawDelta(measures, comparedMeasure);
    const lowerIsBetter: boolean = MP.MeasurePropertiesMap[measures[0].type].lowerIsBetter;
    if (rawDelta === 0) {
        return TrajectoryType.UNCHANGED;
    }
    return lowerIsBetter ? (rawDelta < 0 ? TrajectoryType.IMPROVED : TrajectoryType.WORSENED) : (rawDelta > 0 ? TrajectoryType.IMPROVED : TrajectoryType.WORSENED);
};

export const calculateBandSeverityTrajectory = (measures: MD.Measures, comparedMeasure: MD.Measure = null): TrajectoryType => {
    let firstMeasure: MD.Measure = measures[0];
    comparedMeasure = comparedMeasure ? comparedMeasure : measures[measures.length - 1];
    let resolver: SR.SeverityResolver = MP.MeasurePropertiesMap[firstMeasure.type].severityResolver;
    let startSeverity: SR.Severity = resolver(firstMeasure.value).severity;
    let comparedSeverity: SR.Severity = resolver(comparedMeasure.value).severity;
    return startSeverity === comparedSeverity ? TrajectoryType.UNCHANGED : comparedSeverity < startSeverity ? TrajectoryType.IMPROVED : TrajectoryType.WORSENED;
};

// ---=== Display Helpers ===--- //

const TrajectoryLabel: { [key in TrajectoryType]: string } = {
    [TrajectoryType.WORSENED]: "Worsened",
    [TrajectoryType.UNCHANGED]: "Unchanged",
    [TrajectoryType.IMPROVED]: "Improved"
};

export type DisplayComparedDeltaOptions = {
    precision?: number;
    round?: boolean;
    absolute?: boolean;
    percent?: boolean;
};

export const displayTrajectoryType = (mt: TrajectoryType): string => {
    return TrajectoryLabel[mt] || "Unknown";
};

export const displayComparedDelta = (mt: Trajectory, options: DisplayComparedDeltaOptions = {}): string => {
    const rawValue = mt?.comparedDelta;
    if (rawValue === null || rawValue === undefined || isNaN(rawValue)) {
        return "-"; // Return a placeholder for invalid/missing data
    }
    let valueToFormat = rawValue;
    if (options.absolute) {valueToFormat = Math.abs(valueToFormat);}
    if (options.percent) {valueToFormat *= 100;}
    let formattedValue: string;
    const precision = options.precision;
    if (precision !== undefined && Number.isInteger(precision) && precision >= 0) {
        formattedValue = valueToFormat.toFixed(precision);
    }
    else {
        formattedValue = valueToFormat.toString();
    }
    if (options.percent) {formattedValue += "%";}
    return formattedValue;
};

export const displayBandTrajectorySlope = (mt: Trajectory): string => {
    switch (mt.bandTrajectory) {
        case TrajectoryType.IMPROVED:
            return "has improved to";
        case TrajectoryType.WORSENED:
            return "has worsened to";
        case TrajectoryType.UNCHANGED:
            return "has remained at";
    }
};

export const displayBandSeverityTrajectorySlope = (mt: Trajectory): string => {
    switch (mt.bandSeverityTrajectory) {
        case TrajectoryType.IMPROVED:
            return "has improved to";
        case TrajectoryType.WORSENED:
            return "has worsened to";
        case TrajectoryType.UNCHANGED:
            return "has remained at";
    }
};

export const displayBandTrajectoryApproach = (mt: Trajectory): string => {
    switch (mt.bandTrajectory) {
        case TrajectoryType.IMPROVED:
            return "from";
        case TrajectoryType.WORSENED:
            return "from";
        case TrajectoryType.UNCHANGED:
            return "and";
    }
};

export const displayBandSeverityTrajectoryApproach = (mt: Trajectory): string => {
    switch (mt.bandSeverityTrajectory) {
        case TrajectoryType.IMPROVED:
            return "from";
        case TrajectoryType.WORSENED:
            return "from";
        case TrajectoryType.UNCHANGED:
            return "and";
    }
};

export const displayBandTrajectoryTerseAssessment = (mt: Trajectory, nonfunctional: boolean = false) => {
    const comparedMeasure = mt.comparedMeasure;
    const resolver: SR.SeverityDetails = M.severityResolver(comparedMeasure);
    if (nonfunctional && resolver.severity === SR.Severity.LOW) {
        return "";
    }
    const scoreDescription = M.scoreDescription(comparedMeasure);
    const scoreRangeDisplay = M.scoreRangeDisplay(comparedMeasure);
    return `${scoreDescription ? [scoreDescription, scoreRangeDisplay].join(" ") : mt.measureName} ${displayBandTrajectorySlope(mt)} ${M.displayAsUnits(comparedMeasure)} (${resolver.label}). ${resolver.description.replace(/^\n+/, '')}`;
};

export const displayBandSeverityTrajectoryVerboseAssessment = (mt: Trajectory) => {
    const startMeasure = firstMeasure(mt);
    const startResolver: SR.SeverityDetails = M.severityResolver(startMeasure);
    const comparedMeasure = mt.comparedMeasure;
    const comparisonResolver: SR.SeverityDetails = M.severityResolver(comparedMeasure);
    const scoreDescription = M.scoreDescription(comparedMeasure);
    const scoreRangeDisplay = M.scoreRangeDisplay(comparedMeasure);
    return `${scoreDescription ? [scoreDescription, scoreRangeDisplay].join(" ") : mt.measureName} ${displayBandSeverityTrajectorySlope(mt)} ${M.displayAsUnits(comparedMeasure)} (${comparisonResolver.label}) ${displayBandSeverityTrajectoryApproach(mt)} ${M.displayAsUnits(startMeasure)} (${startResolver.label})`;
};

// Constructor

export const createTrajectory = (processor: TrajectoryProcessor): Trajectory => {
    const measureType: MD.MeasureType = processor.measureType;
    const comparedMeasureRange: number = processor.comparedMeasureRange ?? ComparedRomMeasureRangeDefault;

    // Inputs
    // const measures: MD.Measures = processor.measures;
    const measures: MD.Measures = clipMeasureValues(measureType, processor.measures);
    let comparedMeasure: MD.Measure = null;
    let firstNonWeekZeroMeasure = null;
    // Results
    let rawDelta: number = 0;
    let rawTrajectory: TrajectoryType = null;
    let comparedDelta: number = 0;
    let bandTrajectory: TrajectoryType = null;
    let bandSeverityTrajectory: TrajectoryType = null;
    let normalizedDelta = 0;
    let normalizedValues = null;

    if (measures && measures.length > 0) {
        // Calculate delta between First -> Last measurement
        rawDelta = M.rawDelta(measures);
        // How does last measurement compare with the first? (ie unchanged/improved/worsened)
        rawTrajectory = calculateRawTrajectory(measures);

        // Determine the comparison measurement and calculate the delta between First -> Compared measurement
        if (M.isRomType(measureType)) {
            // ROM measurement will select the best measurement most recent 3 weeks
            comparedMeasure = M.bestRecentRomMeasure(measures, comparedMeasureRange);
            comparedDelta = M.romDelta(measureType, measures[0].value, comparedMeasure.value);
        }
        else {
            // Non-ROM measurements will use the most recent measurement
            comparedMeasure = measures[measures.length - 1];
            comparedDelta = rawDelta;
        }

        // How does the compared measurement compare with the first? (unchanged/improved/worsened)
        bandTrajectory = calculateRawTrajectory(measures, comparedMeasure);
        // How does the score band of the compared measurement compare with the first? (unchanged/improved/worsened)
        bandSeverityTrajectory = calculateBandSeverityTrajectory(measures, comparedMeasure);

        // Normalize the measurement values to a range of 0-100.
        // Used for graphing key improved outcome measures.
        normalizedValues = measures?.map(
            (measure: MD.Measure): number => {
                return M.score(measure);
            }
        );

        // Calculate normalized delta between First -> Last measurement
        // Used for ranking key improved outcome measures.
        normalizedDelta = normalizedValues.length > 0 ? normalizedValues[normalizedValues.length - 1] - normalizedValues[0] : 0;

        // Determine the first non week-zero measurement either from the
        // processor firstMeasurement argument or the processor measurements
        if (M.isRomType(measureType)) {
            if (processor.firstMeasurement !== null && processor.firstMeasurement.plan_week !== 0) {
                // For ROM measures, use the chronologically first measurement
                firstNonWeekZeroMeasure = {
                    type: processor.measureType,
                    week: Number(processor.firstMeasurement.plan_week),
                    value: Math.round(processor.firstMeasurement.first_measurement)
                } as MD.Measure;
            }
            else {
                firstNonWeekZeroMeasure = measures.find(measure => measure.week !== 0);
            }
        }
    }

    return {
        measureType: measureType,
        measureName: M.name(measureType),
        // Inputs
        measures: measures,
        comparedMeasure: comparedMeasure,
        firstNonWeekZeroMeasure: firstNonWeekZeroMeasure,
        // Results
        rawDelta: rawDelta,
        rawTrajectory: rawTrajectory,
        comparedDelta: comparedDelta,
        bandTrajectory: bandTrajectory,
        bandSeverityTrajectory: bandSeverityTrajectory,
        normalizedDelta: normalizedDelta,
        normalizedMeasures: normalizedValues
    } as Trajectory;
};